import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { queryClient } from '@/clients';
import {
    fetchPrefetchedDetailQuery,
    getPrefetchedDetailQueryData,
    workspaceResourceDetailQueryOptions,
    workspaceResourceQueryKeys,
} from '@/queries/tanstack/prefetch';
import { listService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { List } from '@/types';

import { ListActions, ListState } from '../types';

export const createListActions: StateCreator<ListState & ListActions, [['zustand/immer', never]], [], ListActions> = (
    set,
    get,
    store,
) => ({
    // Core
    fetchLists: async (params) => {
        set({ loading: true });

        try {
            const lists = await listService.list(params);

            set({ lists, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load lists' });
        }
    },
    fetchListById: async (id) => {
        const queryOptions = workspaceResourceDetailQueryOptions.lists(id);
        const cachedList = getPrefetchedDetailQueryData(queryClient, queryOptions);

        set({
            loadingList: !cachedList,
            error: undefined,
            ...(cachedList ? { selectedList: cachedList } : {}),
        });

        try {
            const list = await fetchPrefetchedDetailQuery(queryClient, queryOptions);

            set({ selectedList: list, loadingList: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch list detail';
                state.loadingList = false;
            });
        }
    },
    clearSelectedList: () => {
        set((state) => {
            state.selectedList = null;
        });
    },
    createList: async (input) =>
        runSingleFlight(createSingleFlightKey('lists:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as List;

            set({ lists: appendEntity(get().lists, optimistic) });

            try {
                const list = await listService.create(input);

                set({ lists: replaceOptimisticEntityById(get().lists, tempId, list) });

                return list;
            } catch (error) {
                captureException(error);

                set({ lists: removeEntityById(get().lists, tempId), error: 'Failed to create list' });
            }
        }),
    updateList: async (id, updates) => {
        const prev =
            get().lists.find((f) => f.id === id) ?? (get().selectedList?.id === id ? get().selectedList : null);
        if (!prev) {
            return;
        }

        set({
            error: undefined,
            lists: mergeEntityById(get().lists, id, updates),
            selectedList:
                get().selectedList?.id === id ? mergeEntity(get().selectedList!, updates) : get().selectedList,
        });

        try {
            const list = await listService.update(id, updates);

            set({
                lists: mergeEntityById(get().lists, id, list),
                selectedList:
                    get().selectedList?.id === id ? mergeEntity(get().selectedList!, list) : get().selectedList,
            });
            // Re-run lookups after the saved key changes; do not retain pre-save optimistic results.
            void queryClient.invalidateQueries({ queryKey: ['list', id] });
            void queryClient.invalidateQueries({ queryKey: workspaceResourceDetailQueryOptions.lists(id).queryKey });
        } catch (error) {
            captureException(error);

            set({
                lists: replaceEntityById(get().lists, id, prev),
                selectedList: get().selectedList?.id === id ? prev : get().selectedList,
                error: 'Failed to update list',
            });
        }
    },
    deleteList: async (id) => {
        const prev = get().lists;

        set({
            lists: removeEntityById(get().lists, id),
            selectedList: get().selectedList?.id === id ? null : get().selectedList,
        });

        try {
            await listService.delete(id);
        } catch (error) {
            captureException(error);

            set({ lists: prev, error: 'Failed to delete list' });
        }
    },
    applyListDelta: (packet) => {
        if (packet.entity !== 'list') {
            return;
        }

        const next = reduceEntityDelta<List>(get().lists, get().selectedList, packet);

        if (!next) {
            return;
        }

        set({
            lists: next.items,
            selectedList: next.selected,
        });
    },
    // - Aliases
    duplicateList: async (id) => {
        const prev = get().lists;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await listService.duplicate(id);

            set({ lists: upsertEntityById(get().lists, duplicated, { prepend: true }) });

            toast(`List Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: { id: duplicated.id, type: 'list' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ lists: prev, error: 'Failed to duplicate list' });

            toast.error('Failed to duplicate list');
        }
    },
    toggleListFavorite: async (id, favorited) => {
        const prev = get().lists.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const list = state.lists.find((f) => f.id === id);
            if (list) {
                list.favorited = favorited;
            }

            // Also update selectedList if it matches
            if (state.selectedList?.id === id) {
                state.selectedList.favorited = favorited;
            }
        });

        try {
            await listService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'list' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const list = state.lists.find((f) => f.id === id);
                if (list) {
                    list.favorited = prev.favorited;
                }

                // Revert selectedList favorited if it matches
                if (state.selectedList?.id === id) {
                    state.selectedList.favorited = prev.favorited;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleListLock: async (id, locked) => {
        const prev = get().lists.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const list = state.lists.find((f) => f.id === id);
            if (list) {
                list.locked = locked;
            }

            // Also update selectedList if it matches
            if (state.selectedList?.id === id) {
                state.selectedList.locked = locked;
            }
        });

        try {
            await listService.setLocked(id, locked);

            toast(`List ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'list' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const list = state.lists.find((f) => f.id === id);
                if (list) {
                    list.locked = prev.locked;
                }

                // Revert selectedList locked if it matches
                if (state.selectedList?.id === id) {
                    state.selectedList.locked = prev.locked;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleListArchive: async (id, archived) => {
        const prev = get().lists.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const list = state.lists.find((f) => f.id === id);
            if (list) {
                list.archived = archived;
            }

            // Also update selectedList if it matches
            if (state.selectedList?.id === id) {
                state.selectedList.archived = archived;
            }
        });

        try {
            await listService.setArchived(id, archived);

            toast(`List ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'list' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const list = state.lists.find((f) => f.id === id);
                if (list) {
                    list.archived = prev.archived;
                }

                // Revert selectedList archived if it matches
                if (state.selectedList?.id === id) {
                    state.selectedList.archived = prev.archived;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    // List Data Sources
    addListDataSource: async (listId, connectedAccountId) => {
        try {
            await listService.addDataSource(listId, connectedAccountId);
            const list = await listService.getById(listId);
            const lists = upsertEntityById(get().lists, list);

            set({
                lists,
                selectedList: get().selectedList?.id === listId ? list : get().selectedList,
            });

            queryClient.setQueryData(workspaceResourceQueryKeys.lists(listId), list);
            await queryClient.invalidateQueries({ queryKey: workspaceResourceQueryKeys.lists(listId), exact: false });

            return list;
        } catch (error) {
            captureException(error);
        }
    },
    removeListDataSource: async (listId: string, connectedAccountId: string) => {
        try {
            await listService.removeDataSource(listId, connectedAccountId);
            const list = await listService.getById(listId);
            const lists = upsertEntityById(get().lists, list);

            set({
                lists,
                selectedList: get().selectedList?.id === listId ? list : get().selectedList,
            });

            queryClient.setQueryData(workspaceResourceQueryKeys.lists(listId), list);
            await queryClient.invalidateQueries({ queryKey: workspaceResourceQueryKeys.lists(listId), exact: false });
        } catch (error) {
            captureException(error);
        }
    },
});
